import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@platform/ui';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { PageFrame } from '@platform/engine/list/header/PageFrame';
import { tableTitle } from '@platform/engine/list/header/tableTitle';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { ViewPageHead } from '@platform/engine/list/header/ViewPageHead';
import { useRecordListModel } from '@platform/engine/list/useRecordListModel';
import type { IMenuEntry } from '@platform/engine/menu/IMenuEntry';
import { hasView, resolveView } from '@platform/engine/registry/viewRegistry';
import { CustomViewPlaceholder } from '@platform/engine/views/CustomViewPlaceholder';
import type { IInsightViewBodyProps } from './IInsightViewBodyProps';

/**
 * Сторінка аналітичного подання (insight) за прототипом v4: крихти, «Модуль — Таблиця» над заголовком подання, опис подання, рядок
 * «Фільтр» + «Розширені фільтри» і панель тегів і пошуку; тіло — компонент з viewRegistry за ключем сторінки (або заглушка) на моделі
 * списку зі власним ключем стану `page:<ключ>`. Сторінку списку модель не запитує (skipPage): компоненти вантажать свої дані самі.
 */
export function InsightViewBody({ ws, module, rt, page, label }: IInsightViewBodyProps) {
  const { t, i18n } = useTranslation();
  const strings = useModuleStrings(module.module.key);
  const entry = useMemo((): IMenuEntry => ({ key: `page:${page.key}`, label, group: '', view: page.key }), [page.key, label]);
  const model = useRecordListModel(ws, module, rt, entry, page.key, { skipPage: true });
  const moduleName = strings.moduleName(module.module.name);
  const crumbs = useModuleCrumbs(module.module.key, moduleName);
  const descKey = `pageHead.insights.${page.key}`;
  const View = resolveView(page.key);
  const head = (
    <ViewPageHead model={model} eyebrow={tableTitle(moduleName, strings.entity(rt.key, 'name', rt.name))} title={label}
      description={i18n.exists(descKey) ? t(descKey) : undefined} />
  );
  return (
    <PageFrame crumbs={crumbs} head={head}>
      {hasView(page.key) ? <Suspense fallback={<Spinner />}><View model={model} /></Suspense> : <CustomViewPlaceholder name={label} />}
    </PageFrame>
  );
}
